/**
 * @fileoverview Cover-page-tab illustration: the Body ↔ Cover Page toggle states (Phase 5, J3).
 * @module ui-admin/help/illustrations/IllustrationCoverPageTab
 *
 * Responsibilities: - Faithfully recreate the canvas header tab toggle in three states: Body
 *                     active, Cover Page active, and the "+ Add Cover Page" state.
 * Dependencies:     - React; shared illustration primitives.
 * Architecture:     - Presentation-only SVG. No state, no data, no side effects.
 * Notes:            - Mirrors the surface tab strip in DocumentCanvasBuilder.
 */

import React from 'react';
import { CalloutBadge, IllustrationFigure, ILLUS } from './illustrationPrimitives';

/**
 * @component IllustrationCoverPageTab
 * Purpose: Annotated diagram of the Body / Cover Page tab toggle in each state.
 */
export function IllustrationCoverPageTab() {
  return (
    <IllustrationFigure
      title="The Body and Cover Page tab toggle in each state"
      viewBox="0 0 400 130"
      legend={[
        'Body tab active: you are editing the main document body.',
        'Cover Page tab active: you are editing the leading cover page.',
        'No cover page yet: use + Add Cover Page to create one.',
      ]}
    >
      <rect x={2} y={2} width={396} height={126} rx={8} fill={ILLUS.white} stroke={ILLUS.gray200} />

      {/* State 1: Body active */}
      <text x={20} y={28} fontSize={9} fontWeight={600} fill={ILLUS.gray500}>Body active</text>
      <rect x={20} y={34} width={150} height={24} rx={5} fill={ILLUS.gray100} />
      <rect x={24} y={38} width={70} height={16} rx={3} fill={ILLUS.white} stroke={ILLUS.gray200} />
      <text x={59} y={49} textAnchor="middle" fontSize={8} fontWeight={600} fill={ILLUS.gray800}>Body</text>
      <text x={132} y={49} textAnchor="middle" fontSize={8} fill={ILLUS.gray500}>Cover Page</text>

      {/* State 2: Cover Page active */}
      <text x={230} y={28} fontSize={9} fontWeight={600} fill={ILLUS.gray500}>Cover Page active</text>
      <rect x={230} y={34} width={150} height={24} rx={5} fill={ILLUS.gray100} />
      <text x={257} y={49} textAnchor="middle" fontSize={8} fill={ILLUS.gray500}>Body</text>
      <rect x={300} y={38} width={76} height={16} rx={3} fill={ILLUS.white} stroke={ILLUS.gray200} />
      <text x={338} y={49} textAnchor="middle" fontSize={8} fontWeight={600} fill={ILLUS.gray800}>Cover Page</text>

      {/* State 3: Add Cover Page */}
      <text x={20} y={88} fontSize={9} fontWeight={600} fill={ILLUS.gray500}>No cover page yet</text>
      <rect x={20} y={94} width={70} height={22} rx={5} fill={ILLUS.gray100} />
      <rect x={24} y={98} width={62} height={14} rx={3} fill={ILLUS.white} stroke={ILLUS.gray200} />
      <text x={55} y={108} textAnchor="middle" fontSize={8} fontWeight={600} fill={ILLUS.gray800}>Body</text>
      <rect x={100} y={94} width={96} height={22} rx={5} fill={ILLUS.white} stroke={ILLUS.gray300} />
      <text x={148} y={108} textAnchor="middle" fontSize={8} fill={ILLUS.gray700}>+ Add Cover Page</text>

      {/* Callouts */}
      <CalloutBadge n={1} x={59} y={38} />
      <CalloutBadge n={2} x={338} y={38} />
      <CalloutBadge n={3} x={148} y={98} />
    </IllustrationFigure>
  );
}
